import { cloneElement, type ReactElement, type ReactNode, useEffect, useState } from 'react';
import { matchPath, Params, type RouteObject, useLocation, useParams, useSearchParams } from 'react-router-dom';

import { AuthInfoIdValidate, getAuthInfoIdValidate } from '@/api/pc-api/v1/auth/auth-info-id/validate';
import PageLoading from '@/components/Loading/PageLoading';
import { BizTargetType } from '@/constants/bizTargetType';
import { BizType } from '@/constants/bizType';
import { Role } from '@/constants/role';
import { ROUTES } from '@/constants/routes';
import usePrefixNavigate from '@/hooks/usePrefixNavigate';
import { AuthValidation } from '@/hooks/usePreLoaderData';
import InternalError from '@/pages/error/internal';
import { useAuthStore } from '@/store/auth';

enum LoaderStatus {
  LOADING = 'LOADING',
  FAILURE = 'FAILURE',
  SUCCESS = 'SUCCESS'
}

const withAuthValidation = (element: ReactNode) => {
  return function EnhancedComponent({
    allowedTypes,
    allowedRoles,
    allowedBizTypes,
    preLoader
  }: {
    allowedTypes?: BizTargetType[];
    allowedRoles?: Role[];
    allowedBizTypes?: BizType[];
    preLoader?: PreLoaderFunction;
  }) {
    const location = useLocation();
    const { navigate } = usePrefixNavigate();
    const { AUTH_INFO_ID = '' } = useParams();
    const params = useParams();
    const [search] = useSearchParams();
    const { setAuthInfo, setAuthInfoId } = useAuthStore();
    const [loaderStatus, setLoaderStatus] = useState<LoaderStatus>(LoaderStatus.LOADING);
    const [preLoaderData, setPreloaderData] = useState(null);

    useEffect(() => {
      setAuthInfoId(AUTH_INFO_ID);

      (async () => {
        setLoaderStatus(LoaderStatus.LOADING);
        const { data } = await getAuthInfoIdValidate(AUTH_INFO_ID);
        const { newAuthInfoId, bizTargetType, role, bizType } = data.data;

        // 새로운 인가정보가 있을 경우 새로운 인가정보로 이동
        if (newAuthInfoId && newAuthInfoId !== AUTH_INFO_ID) {
          window.location.href = window.location.href.replace(AUTH_INFO_ID, newAuthInfoId);
          return;
        }

        // 허용되지 않은 bizTargetType은 not-found로 이동
        if (allowedTypes && !allowedTypes.includes(bizTargetType)) {
          navigate(ROUTES.ERROR.NOT_FOUND, { replace: true });
          return;
        }

        // 허용되지 않은 role은 not-found로 이동
        if (allowedRoles && !allowedRoles.includes(role)) {
          navigate(ROUTES.ERROR.NOT_FOUND, { replace: true });
          return;
        }

        // 허용되지 않은 bizType은 not-found로 이동
        if (allowedBizTypes && !allowedBizTypes.includes(bizType)) {
          navigate(ROUTES.ERROR.NOT_FOUND, { replace: true });
          return;
        }

        setAuthInfo(data.data);

        if (!preLoader) {
          setLoaderStatus(LoaderStatus.SUCCESS);
          return;
        }

        preLoader({ authData: data.data, params, search })
          .then((fethcedPreloaderData) => {
            setPreloaderData(fethcedPreloaderData);
            setLoaderStatus(LoaderStatus.SUCCESS);
          })
          .catch(() => {
            setLoaderStatus(LoaderStatus.FAILURE);
          });
      })();
    }, [AUTH_INFO_ID]);

    switch (loaderStatus) {
      case LoaderStatus.LOADING:
        return <PageLoading />;
      case LoaderStatus.FAILURE: {
        location.state = { ...location.state, returnUrl: window.location.href };
        if (matchPath(ROUTES.SETTLE.BASE_INFO, location.pathname)) return null;
        return <InternalError />;
      }
      case LoaderStatus.SUCCESS: {
        return (
          <AuthValidation.Provider value={preLoaderData}>
            {cloneElement(element as ReactElement)}
          </AuthValidation.Provider>
        );
      }
      default:
        return null;
    }
  };
};

export type PreLoaderFunction = (args: {
  authData: AuthInfoIdValidate;
  params: Params;
  search: URLSearchParams;
}) => Promise<any>;
export type ExtendedRouteObject = RouteObject & {
  preLoader?: PreLoaderFunction;
  children?: ExtendedRouteObject[];
  allowedBizTypes?: BizType[];
};

type CreatePrivateRoutesParams = {
  routes: ExtendedRouteObject[];
} & (
  | {
      allowedTypes: BizTargetType[];
      allowedRoles: Role[];
    }
  | {
      allowedTypes: BizTargetType[];
      allowedRoles?: never;
    }
  | {
      allowedTypes?: never;
      allowedRoles: Role[];
    }
);

export const createPrivateRoutes = ({
  allowedTypes,
  allowedRoles,
  routes
}: CreatePrivateRoutesParams): RouteObject[] => {
  return routes.map((route: ExtendedRouteObject) => {
    const { element, children, allowedBizTypes, preLoader, ...rest } = route;

    const EnhancedComponent = withAuthValidation(element);
    // 자식 노드도 HOC를 거치도록 재귀적으로 호출
    const routeChildren = children
      ? createPrivateRoutes({
          allowedTypes,
          allowedRoles,
          routes: children
        } as CreatePrivateRoutesParams)
      : undefined;

    // IndexRouteObject 혹은 NonIndexRouteObject 타입에 따라 index 처리
    if ('index' in route && route.index === true) {
      return {
        ...rest,
        element: (
          <EnhancedComponent
            allowedTypes={allowedTypes}
            allowedRoles={allowedRoles}
            allowedBizTypes={allowedBizTypes}
            preLoader={preLoader}
          />
        ),
        children: undefined,
        index: true
      };
    }

    return {
      ...rest,
      element: (
        <EnhancedComponent
          allowedTypes={allowedTypes}
          allowedRoles={allowedRoles}
          allowedBizTypes={allowedBizTypes}
          preLoader={preLoader}
        />
      ),
      children: routeChildren,
      index: false
    };
  });
};
